import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dispatchAPI, crmJobsAPI, usersAPI, techBlockedTimeAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { QuickAddMaterialModal } from '../components/QuickAddMaterialModal';
import { PrepareTruckDrawer } from '../components/PrepareTruckDrawer';
import {
  Plus, Loader2, ChevronLeft, ChevronRight, Clock, User, Briefcase, Trash2, X,
  Package, Truck, Wrench, AlertTriangle, Settings, CalendarDays, CalendarRange,
  Filter, MapPin, MessageSquare, Shield, DollarSign, Ban, Search, ChevronsUpDown, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { EventHoverCard } from '../components/EventHoverCard';

/* =========================================================================
   CONSTANTS
   ========================================================================= */

const EVENT_STATUSES = [
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'en_route', label: 'En Route', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'on_site', label: 'On Site', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'done', label: 'Done', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
];

const STATUS_COLORS = {
  scheduled: 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20',
  en_route: 'border-l-purple-500 bg-purple-50/50 dark:bg-purple-950/20',
  on_site: 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20',
  done: 'border-l-green-500 bg-green-50/50 dark:bg-green-950/20',
};

const ESTIMATE_STATUS_COLOR = 'border-l-teal-500 bg-teal-50/50 dark:bg-teal-950/20';

const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0 (12 AM) to 23 (11 PM)

import { getTenantTimezone, parseUTC } from '../lib/timezone';
import { MobileFAB } from '../components/MobileFAB';

const DEFAULT_DISPLAY = { showCustomer: true, showJobName: true, showTime: true, showTech: true, showPrivateNotes: false };

/* Payment status badge styles (text + color, no color-only reliance) */
const PAYMENT_BADGE = {
  paid: { label: 'Paid', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  partial: { label: 'Partial', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  unpaid: { label: 'Unpaid', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

/* Maintenance plan status label map */
const PLAN_BADGE_DISPATCH = {
  active: { label: 'Plan', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  paused: { label: 'Plan (Paused)', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  expired: { label: 'Plan (Exp)', className: 'bg-gray-200 text-gray-700 dark:bg-gray-800/40 dark:text-gray-400' },
  canceled: { label: 'Plan (Canceled)', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
];

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

const TechAvatar = ({ tech, avatarColor }) => {
  const [imgErr, setImgErr] = React.useState(false);
  const photoSrc = tech.photo_url && !imgErr
    ? (tech.photo_url.startsWith('http') ? tech.photo_url : `${process.env.REACT_APP_BACKEND_URL}${tech.photo_url}`)
    : null;
  return photoSrc ? (
    <img src={photoSrc} alt={tech.name} className="w-8 h-8 rounded-full object-cover shrink-0" onError={() => setImgErr(true)} />
  ) : (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${avatarColor}`}>
      {getInitials(tech.name)}
    </div>
  );
};

/* =========================================================================
   HELPERS
   ========================================================================= */

function formatTime(dt) {
  if (!dt) return '';
  const tz = getTenantTimezone();
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).format(parseUTC(dt));
}

function getDateStr(date) {
  return date.toISOString().split('T')[0];
}

function getWeekDates(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - day); // Sunday
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(start);
    dd.setDate(start.getDate() + i);
    return getDateStr(dd);
  });
}

function formatDayLabel(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatDateHeader(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function EventStatusBadge({ status }) {
  const opt = EVENT_STATUSES.find((o) => o.value === status) || EVENT_STATUSES[0];
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${opt.color}`}>{opt.label}</span>;
}

/** Format a snapped decimal hour (e.g. 9.5) to "9:30 AM" */
function formatSnapTime(decimalHour) {
  const h = Math.floor(decimalHour);
  const m = Math.round((decimalHour % 1) * 60);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/**
 * Convert a tenant-local date/time to a UTC Date object.
 * The dispatch grid shows hours in tenant-local time; we need UTC for storage.
 *
 * @param {string} dateStr YYYY-MM-DD
 * @param {number} hours 0-23 in tenant-local
 * @param {number} minutes 0-59
 * @returns {Date} UTC Date
 */
function localToUTC(dateStr, hours, minutes) {
  const tz = getTenantTimezone();
  const y = parseInt(dateStr.slice(0, 4));
  const mo = parseInt(dateStr.slice(5, 7)) - 1;
  const d = parseInt(dateStr.slice(8, 10));
  // Guess: set UTC to the same numeric values
  const guess = new Date(Date.UTC(y, mo, d, hours, minutes, 0));
  // See what tenant-local time this UTC value maps to
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(guess);
  const getN = (type) => parseInt(parts.find(p => p.type === type)?.value || '0');
  const localH = getN('hour') === 24 ? 0 : getN('hour');
  const localM = getN('minute');
  // Diff: we want the local display to be hours:minutes, but guess shows localH:localM
  const diffMin = (hours * 60 + minutes) - (localH * 60 + localM);
  return new Date(guess.getTime() + diffMin * 60000);
}

/** Format a UTC Date to a naive ISO string for the backend (YYYY-MM-DDThh:mm:00) */
function formatUTCNaive(utcDate) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${utcDate.getUTCFullYear()}-${pad(utcDate.getUTCMonth() + 1)}-${pad(utcDate.getUTCDate())}T${pad(utcDate.getUTCHours())}:${pad(utcDate.getUTCMinutes())}:00`;
}

/* =========================================================================
   MAIN COMPONENT
   ========================================================================= */


/* =========================================================================
   JOB SEARCH PICKER — Searchable combobox for the Schedule drawer
   ========================================================================= */
function JobSearchPicker({ jobs, value, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected = jobs.find(j => j.id === value);

  const filtered = useMemo(() => {
    const src = jobs.slice(0, 100);
    if (!search.trim()) return src;
    const q = search.toLowerCase();
    return src.filter(j =>
      (j.job_number || '').toLowerCase().includes(q) ||
      (j.job_name || '').toLowerCase().includes(q) ||
      (j.customer_name || '').toLowerCase().includes(q)
    );
  }, [jobs, search]);

  return (
    <div>
      <Label className="text-sm font-medium">Job *</Label>
      <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(''); }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-9 font-normal text-sm"
            data-testid="schedule-job-picker"
          >
            {selected ? (
              <span className="truncate text-left">
                <span className="font-semibold">#{selected.job_number}</span>
                {selected.customer_name && <span className="text-muted-foreground"> — {selected.customer_name}</span>}
                {selected.job_name && <span className="text-muted-foreground/70"> · {selected.job_name}</span>}
              </span>
            ) : (
              <span className="text-muted-foreground">Search jobs...</span>
            )}
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" side="bottom" sideOffset={4}>
          <div className="flex items-center border-b px-3 py-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-7 w-full rounded-md bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              placeholder="Search by job #, customer, or title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="job-search-input"
              autoFocus
            />
          </div>
          <div className="max-h-[400px] overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No jobs found.</p>
            ) : (
              filtered.map(j => (
                <div
                  key={j.id}
                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-accent transition-colors ${value === j.id ? 'bg-accent' : ''}`}
                  onClick={() => { onSelect(j.id); setOpen(false); setSearch(''); }}
                  data-testid={`job-option-${j.id}`}
                >
                  <Check className={`h-3.5 w-3.5 shrink-0 ${value === j.id ? 'opacity-100 text-primary' : 'opacity-0'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-xs">#{j.job_number}</span>
                      {j.customer_name && (
                        <span className="text-xs text-muted-foreground truncate">{j.customer_name}</span>
                      )}
                    </div>
                    {j.job_name && (
                      <p className="text-[11px] text-muted-foreground/70 truncate">{j.job_name}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}


export default function Dispatch() {
  const navigate = useNavigate();
  const { isOwner, isOffice, hasEntitlement } = useAuth();
  const canEdit = isOwner || isOffice;
  const hasCrm = hasEntitlement('crm');

  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [techs, setTechs] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [unscheduledJobs, setUnscheduledJobs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => getCurrentDateStr());
  const [viewMode, setViewMode] = useState('day'); // 'day' | 'week'
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [quickAddJobId, setQuickAddJobId] = useState(null);
  const [prepareTruckJobId, setPrepareTruckJobId] = useState(null);
  const [readinessMap, setReadinessMap] = useState({});
  const [techFilter, setTechFilter] = useState([]); // empty = show all
  const [displayPrefs, setDisplayPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dispatch_display_prefs')) || DEFAULT_DISPLAY; }
    catch { return DEFAULT_DISPLAY; }
  });
  const [form, setForm] = useState({
    job_id: '', assigned_user_id: '', start_time: '08:00', end_time: '09:00', notes: '',
  });
  const [dragOver, setDragOver] = useState(null); // { date, hour } for visual feedback
  const [techDragOver, setTechDragOver] = useState(null); // techId being hovered during drag
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [blockedTimeDrawerOpen, setBlockedTimeDrawerOpen] = useState(false);
  const [blockedTimeForm, setBlockedTimeForm] = useState({ user_id: '', title: '', block_type: 'pto', start_at: '', end_at: '', all_day: false, notes: '' });
  const [savingBlockedTime, setSavingBlockedTime] = useState(false);

  // Compute date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'week') {
      const days = getWeekDates(selectedDate);
      return { start: days[0], end: days[6], days };
    }
    return { start: selectedDate, end: selectedDate, days: [selectedDate] };
  }, [selectedDate, viewMode]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dispatchAPI.listEvents({ start_date: dateRange.start, end_date: dateRange.end });
      setEvents(res.data.events || []);
    } catch (err) {
      if (err?.response?.data?.detail?.code === 'ENTITLEMENT_REQUIRED') {
        toast.error('Dispatch module is not enabled.');
      } else {
        toast.error('Failed to load dispatch events');
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch unscheduled jobs
  const fetchUnscheduled = useCallback(async () => {
    try {
      const res = await dispatchAPI.unscheduledJobs({ start_date: dateRange.start, end_date: dateRange.end });
      setUnscheduledJobs(res.data.jobs || []);
    } catch { setUnscheduledJobs([]); }
  }, [dateRange]);

  useEffect(() => { fetchEvents(); fetchUnscheduled(); fetchBlockedTimes(); }, [fetchEvents, fetchUnscheduled]);

  // Fetch blocked times for the current date range
  const fetchBlockedTimes = useCallback(async () => {
    try {
      const res = await techBlockedTimeAPI.list({ start_date: dateRange.start, end_date: dateRange.end });
      setBlockedTimes(res.data.items || []);
    } catch { setBlockedTimes([]); }
  }, [dateRange]);

  const handleSaveBlockedTime = async () => {
    if (!blockedTimeForm.user_id || !blockedTimeForm.title || !blockedTimeForm.start_at || !blockedTimeForm.end_at) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSavingBlockedTime(true);
    try {
      await techBlockedTimeAPI.create(blockedTimeForm);
      toast.success('Blocked time created');
      setBlockedTimeDrawerOpen(false);
      setBlockedTimeForm({ user_id: '', title: '', block_type: 'pto', start_at: '', end_at: '', all_day: false, notes: '' });
      fetchBlockedTimes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create blocked time');
    } finally { setSavingBlockedTime(false); }
  };

  const handleDeleteBlockedTime = async (id) => {
    try {
      await techBlockedTimeAPI.delete(id);
      toast.success('Blocked time removed');
      fetchBlockedTimes();
    } catch { toast.error('Failed to delete'); }
  };

  // Fetch techs and jobs for the schedule drawer
  useEffect(() => {
    const loadData = async () => {
      try {
        const [techRes, jobRes] = await Promise.all([
          usersAPI.list({ include_archived: false }),
          crmJobsAPI.list({ page_size: 100 }),
        ]);
        setTechs((techRes.data?.users || techRes.data || []).filter(u => u.role === 'tech' || u.role === 'owner' || u.role === 'office'));
        setJobs(jobRes.data?.jobs || []);
      } catch { /* non-critical */ }
    };
    loadData();
  }, []);

  // Readiness data
  useEffect(() => {
    if (!events.length) return;
    const jobIds = [...new Set(events.map(e => e.job_id).filter(Boolean))];
    const fetchAll = async () => {
      const map = {};
      await Promise.allSettled(
        jobIds.map(async (jid) => {
          try {
            const res = await crmJobsAPI.readiness(jid);
            if (res.data?.status !== 'no_kit') map[jid] = res.data;
          } catch { /* skip */ }
        })
      );
      setReadinessMap(map);
    };
    fetchAll();
  }, [events]);

  // Save display prefs
  useEffect(() => {
    localStorage.setItem('dispatch_display_prefs', JSON.stringify(displayPrefs));
  }, [displayPrefs]);

  // Date navigation
  const shiftDate = (dir) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + (viewMode === 'week' ? dir * 7 : dir));
    setSelectedDate(getDateStr(d));
  };

  // Filter events by tech
  const filteredEvents = useMemo(() => {
    if (techFilter.length === 0) return events;
    return events.filter(e => techFilter.includes(e.assigned_user_id) || (!e.assigned_user_id && techFilter.includes('unassigned')));
  }, [events, techFilter]);

  // Group events by tenant-local date for week view
  const eventsByDate = useMemo(() => {
    const tz = getTenantTimezone();
    const map = {};
    dateRange.days.forEach(d => { map[d] = []; });
    filteredEvents.forEach(e => {
      // Convert start_at to tenant-local date
      const d = parseUTC(e.start_at);
      const localDate = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(d); // YYYY-MM-DD
      if (map[localDate]) map[localDate].push(e);
    });
    return map;
  }, [filteredEvents, dateRange]);

  // Track techs with currently active (in-progress) events
  const activeTechIds = useMemo(() => {
    const ids = new Set();
    events.forEach(evt => {
      if (isEventActive(evt) && evt.assigned_user_id) {
        ids.add(evt.assigned_user_id);
      }
    });
    return ids;
  }, [events]);

  // Event drawer
  const openCreateDrawer = () => {
    setEditingEvent(null);
    setForm({ job_id: '', assigned_user_id: '', scheduled_date: selectedDate, start_time: '08:00', end_time: '09:00', notes: '' });
    setDrawerOpen(true);
  };

  const openEditDrawer = (evt) => {
    setEditingEvent(evt);
    const startT = parseUTC(evt.start_at);
    const endT = parseUTC(evt.end_at);
    const tz = getTenantTimezone();
    const startInTz = new Date(startT.toLocaleString('en-US', { timeZone: tz }));
    const dateStr = `${startInTz.getFullYear()}-${String(startInTz.getMonth()+1).padStart(2,'0')}-${String(startInTz.getDate()).padStart(2,'0')}`;
    setForm({
      job_id: evt.job_id,
      assigned_user_id: evt.assigned_user_id || '',
      scheduled_date: dateStr,
      start_time: `${String(startInTz.getHours()).padStart(2,'0')}:${String(startInTz.getMinutes()).padStart(2,'0')}`,
      end_time: (() => { const e = new Date(endT.toLocaleString('en-US', { timeZone: tz })); return `${String(e.getHours()).padStart(2,'0')}:${String(e.getMinutes()).padStart(2,'0')}`; })(),
      notes: evt.notes || '',
      status: evt.status,
    });
    setDrawerOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingEvent && !form.job_id) { toast.error('Select a job'); return; }
    if (!form.scheduled_date) { toast.error('Select a date'); return; }
    if (!form.start_time || !form.end_time) { toast.error('Start and end times are required'); return; }
    if (form.end_time <= form.start_time) { toast.error('End time must be after start time'); return; }
    setSaving(true);
    try {
      // Convert tenant-local form values → UTC for storage
      const [sH, sM] = form.start_time.split(':').map(Number);
      const [eH, eM] = form.end_time.split(':').map(Number);
      const start_at = formatUTCNaive(localToUTC(form.scheduled_date, sH, sM));
      const end_at = formatUTCNaive(localToUTC(form.scheduled_date, eH, eM));

      if (editingEvent) {
        const res = await dispatchAPI.updateEvent(editingEvent.id, {
          assigned_user_id: form.assigned_user_id || null,
          start_at, end_at,
          status: form.status || editingEvent.status,
          notes: form.notes || null,
        });
        toast.success('Event updated');
        if (res.data?.warnings?.length) {
          res.data.warnings.forEach(w => toast.warning(w.message, { duration: 6000 }));
        }
      } else {
        const res = await dispatchAPI.createEvent({
          job_id: form.job_id,
          assigned_user_id: form.assigned_user_id || null,
          start_at, end_at,
          notes: form.notes || null,
        });
        toast.success('Event scheduled');
        if (res.data?.warnings?.length) {
          res.data.warnings.forEach(w => toast.warning(w.message, { duration: 6000 }));
        }
      }
      setDrawerOpen(false);
      fetchEvents();
      fetchUnscheduled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    try {
      await dispatchAPI.deleteEvent(eventId);
      toast.success('Event deleted');
      setDeleteConfirm(null);
      fetchEvents();
      fetchUnscheduled();
    } catch { toast.error('Failed to delete event'); }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await dispatchAPI.updateEvent(eventId, { status: newStatus });
      toast.success('Status updated');
      fetchEvents();
    } catch { toast.error('Failed to update status'); }
  };

  const [sendingOtwId, setSendingOtwId] = useState(null);
  const handleSendOtwText = async (eventId) => {
    setSendingOtwId(eventId);
    try {
      const res = await dispatchAPI.sendOtwText(eventId);
      toast.success(`On-the-way text sent to ${res.data.sent_to}`);
      if (res.data.eta_warning) {
        toast.info(res.data.eta_warning);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send on-the-way text';
      toast.error(detail);
    } finally {
      setSendingOtwId(null);
    }
  };

  const toggleTechFilter = (techId) => {
    setTechFilter(prev =>
      prev.includes(techId) ? prev.filter(id => id !== techId) : [...prev, techId]
    );
  };

  // ---- Drag & Drop ----

  const handleDragStartEvent = useCallback((e, evt) => {
    const durationMs = parseUTC(evt.end_at).getTime() - parseUTC(evt.start_at).getTime();
    e.dataTransfer.setData('application/dispatch-event', JSON.stringify({
      type: 'event', id: evt.id, durationMs, jobId: evt.job_id,
      assignedUserId: evt.assigned_user_id || null,
    }));
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragStartUnscheduled = useCallback((e, job) => {
    e.dataTransfer.setData('application/dispatch-event', JSON.stringify({
      type: 'unscheduled', jobId: job.id, jobNumber: job.job_number,
    }));
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  // Drop onto a technician name in the sidebar → reassign
  const handleTechDrop = useCallback(async (e, targetTechId) => {
    e.preventDefault();
    setTechDragOver(null);
    const raw = e.dataTransfer.getData('application/dispatch-event');
    if (!raw) return;
    const data = JSON.parse(raw);

    if (data.type !== 'event') {
      toast.error('Drag an existing event onto a technician to reassign.');
      return;
    }

    const newAssigned = targetTechId === 'unassigned' ? null : targetTechId;
    if (data.assignedUserId === newAssigned) return; // no-op

    try {
      const res = await dispatchAPI.updateEvent(data.id, { assigned_user_id: newAssigned || '' });
      const techName = newAssigned
        ? techs.find(t => t.id === newAssigned)?.name || 'technician'
        : 'Unassigned';
      toast.success(`Event reassigned to ${techName}`);
      if (res.data?.warnings?.length) {
        res.data.warnings.forEach(w => toast.warning(w.message, { duration: 6000 }));
      }
      fetchEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reassign');
    }
  }, [fetchEvents, techs]);

  const handleTechDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleCalendarDrop = useCallback(async (e, targetDate, gridEl) => {
    e.preventDefault();
    setDragOver(null);
    const raw = e.dataTransfer.getData('application/dispatch-event');
    if (!raw) return;

    let data;
    try { data = JSON.parse(raw); } catch { toast.error('Invalid drag data'); return; }

    // Compute drop time from mouse Y relative to grid (tenant-local hours)
    const rect = gridEl.getBoundingClientRect();
    const yRatio = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const rawHour = GRID_START + yRatio * GRID_HOURS;
    // Snap to 30-min
    const snappedHour = Math.round(rawHour * 2) / 2;
    const startH = Math.floor(snappedHour);
    const startM = Math.round((snappedHour % 1) * 60);

    // Convert tenant-local drop time → UTC for storage
    const startUTC = localToUTC(targetDate, startH, startM);
    const startStr = formatUTCNaive(startUTC);

    if (data.type === 'event') {
      // Reschedule existing event — preserve duration
      const durationMs = data.durationMs || 3600000;
      const endUTC = new Date(startUTC.getTime() + durationMs);
      const endStr = formatUTCNaive(endUTC);

      try {
        const res = await dispatchAPI.updateEvent(data.id, { start_at: startStr, end_at: endStr });
        toast.success('Event rescheduled');
        if (res.data?.warnings?.length) {
          res.data.warnings.forEach(w => toast.warning(w.message, { duration: 6000 }));
        }
        fetchEvents();
      } catch (err) {
        console.error('[Dispatch] Reschedule failed:', err?.response?.data || err);
        toast.error(err?.response?.data?.detail || 'Failed to reschedule event');
      }
    } else if (data.type === 'unscheduled') {
      // Schedule new event — default 1-hour
      const endUTC = new Date(startUTC.getTime() + 3600000);
      const endStr = formatUTCNaive(endUTC);

      // Determine technician from the column drop target (data attribute on grid)
      const techId = gridEl.dataset?.techId || null;

      const payload = {
        job_id: data.jobId,
        start_at: startStr,
        end_at: endStr,
        ...(techId && techId !== 'unassigned' ? { assigned_user_id: techId } : {}),
      };

      try {
        const res = await dispatchAPI.createEvent(payload);
        const techName = techId && techId !== 'unassigned'
          ? techs.find(t => t.id === techId)?.name || 'technician'
          : null;
        toast.success(techName ? `Job #${data.jobNumber} scheduled and assigned to ${techName}` : `Job #${data.jobNumber} scheduled`);
        if (res.data?.warnings?.length) {
          res.data.warnings.forEach(w => toast.warning(w.message, { duration: 6000 }));
        }
        fetchEvents();
        fetchUnscheduled();
      } catch (err) {
        console.error('[Dispatch] Schedule unscheduled job failed:', err?.response?.data || err);
        toast.error(err?.response?.data?.detail || 'Failed to schedule job — please try again');
      }
    }
  }, [fetchEvents, fetchUnscheduled, techs]);

  const handleCalendarDragOver = useCallback((e, date, gridEl) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = e.dataTransfer.types.includes('application/dispatch-event') ? 'move' : 'none';
    // Compute hover time for visual feedback
    const rect = gridEl.getBoundingClientRect();
    const yRatio = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const rawHour = GRID_START + yRatio * GRID_HOURS;
    const snapped = Math.round(rawHour * 2) / 2;
    setDragOver({ date, hour: snapped });
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(null);
  }, []);

  return (
    <Layout>
      <div className="space-y-4" data-testid="dispatch-calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="dispatch-title">Dispatch Calendar</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} {viewMode === 'week' ? 'this week' : 'today'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="dispatch-settings-btn">
                <Settings className="w-4 h-4" />
              </Button>
            </TooltipTrigger><TooltipContent>Calendar display settings</TooltipContent></Tooltip></TooltipProvider>

            {/* View mode toggle */}
            <div className="flex items-center border rounded-md">
              <Button
                variant={viewMode === 'day' ? 'default' : 'ghost'}
                size="sm" className="rounded-r-none"
                onClick={() => setViewMode('day')}
                data-testid="view-day-btn"
              >
                <CalendarDays className="w-4 h-4 mr-1" /> Day
              </Button>
              <Button
                variant={viewMode === 'week' ? 'default' : 'ghost'}
                size="sm" className="rounded-l-none"
                onClick={() => setViewMode('week')}
                data-testid="view-week-btn"
              >
                <CalendarRange className="w-4 h-4 mr-1" /> Week
              </Button>
            </div>

            {canEdit && (
              <Button onClick={openCreateDrawer} data-testid="schedule-event-btn" className="hidden md:inline-flex">
                <Plus className="w-4 h-4 mr-2" /> Schedule
              </Button>
            )}
            {canEdit && (
              <Button variant="outline" onClick={() => { setBlockedTimeForm(f => ({ ...f, start_at: `${selectedDate}T09:00`, end_at: `${selectedDate}T17:00` })); setBlockedTimeDrawerOpen(true); }} data-testid="block-time-btn" className="hidden md:inline-flex">
                <Ban className="w-4 h-4 mr-2" /> Block Time
              </Button>
            )}
          </div>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center gap-3" data-testid="date-nav">
          <Button variant="outline" size="sm" onClick={() => shiftDate(-1)} data-testid="prev-btn">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-40" data-testid="date-picker" />
            <span className="text-sm font-medium hidden sm:inline">
              {viewMode === 'week'
                ? `${formatDayLabel(dateRange.start)} — ${formatDayLabel(dateRange.end)}`
                : formatDateHeader(selectedDate)}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={() => shiftDate(1)} data-testid="next-btn">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedDate(getCurrentDateStr())} data-testid="today-btn">Today</Button>
        </div>

        {/* Main Grid: Sidebar + Calendar */}
        <div className="flex gap-4">
          {/* Left Sidebar: Tech Filters */}
          <div className="w-52 shrink-0 hidden lg:block space-y-3" data-testid="tech-filter-sidebar">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Filter className="w-3 h-3" /> Technicians
              </h3>
              {techFilter.length > 0 && (
                <Button variant="ghost" size="sm" className="h-5 text-xs px-1" onClick={() => setTechFilter([])} data-testid="clear-tech-filter">
                  Clear
                </Button>
              )}
            </div>
            <div className="space-y-1.5">
              {techs.map((t, idx) => {
                const isActive = activeTechIds.has(t.id);
                const avatarColor = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                const isDragTarget = techDragOver === t.id;
                const isVisible = techFilter.length === 0 || techFilter.includes(t.id);
                return (
                  <button
                    key={t.id}
                    onClick={() => toggleTechFilter(t.id)}
                    onDrop={(e) => handleTechDrop(e, t.id)}
                    onDragOver={(e) => { handleTechDragOver(e); setTechDragOver(t.id); }}
                    onDragLeave={() => setTechDragOver(null)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all ${
                      isDragTarget
                        ? 'ring-2 ring-primary bg-primary/10 shadow-md'
                        : isVisible
                          ? 'bg-card border border-border/50 shadow-sm hover:shadow-md hover:border-border'
                          : 'border border-transparent opacity-50 hover:opacity-100 hover:bg-muted/50'
                    }`}
                    data-testid={`tech-filter-${t.id}`}
                  >
                    <TechAvatar tech={t} avatarColor={avatarColor} />
                    <div className="min-w-0 flex-1 text-left">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-[13px] truncate">{t.name}</span>
                        {isActive && (
                          <span className="w-2 h-2 rounded-full bg-green-500 shrink-0 animate-pulse" title="Active job" />
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground capitalize leading-tight">{t.role}</p>
                    </div>
                  </button>
                );
              })}
              <button
                onClick={() => toggleTechFilter('unassigned')}
                onDrop={(e) => handleTechDrop(e, 'unassigned')}
                onDragOver={(e) => { handleTechDragOver(e); setTechDragOver('unassigned'); }}
                onDragLeave={() => setTechDragOver(null)}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all ${
                  techDragOver === 'unassigned'
                    ? 'ring-2 ring-primary bg-primary/10 shadow-md'
                    : techFilter.length === 0 || techFilter.includes('unassigned')
                      ? 'bg-card border border-border/50 shadow-sm hover:shadow-md hover:border-border'
                      : 'border border-transparent opacity-50 hover:opacity-100 hover:bg-muted/50'
                }`}
                data-testid="tech-filter-unassigned"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 bg-muted text-muted-foreground">
                  <User className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <span className="font-medium text-[13px] truncate">Unassigned</span>
                  <p className="text-[10px] text-muted-foreground leading-tight">No tech</p>
                </div>
              </button>
            </div>

            {/* Unscheduled Jobs */}
            {unscheduledJobs.length > 0 && (
              <div className="pt-3 border-t" data-testid="unscheduled-jobs-section">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                  <Briefcase className="w-3 h-3" /> Unscheduled ({unscheduledJobs.length})
                </h3>
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-0.5">
                  {unscheduledJobs.map(j => (
                    <TooltipProvider key={j.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            draggable
                            onDragStart={(e) => handleDragStartUnscheduled(e, j)}
                            onClick={() => navigate(`/jobs/${j.id}`)}
                            className="w-full text-left px-2.5 py-2 rounded-lg bg-card border border-border/50 shadow-sm hover:shadow-md hover:border-border transition-all cursor-grab active:cursor-grabbing active:shadow-lg active:scale-[1.02] border-l-[3px] border-l-amber-400"
                            data-testid={`unscheduled-job-${j.id}`}
                          >
                            <p className="text-xs font-semibold truncate">#{j.job_number}</p>
                            {j.customer_name && <p className="text-[10px] text-muted-foreground truncate leading-tight mt-0.5">{j.customer_name}</p>}
                            {j.job_name && <p className="text-[10px] text-muted-foreground/70 truncate leading-tight">{j.job_name}</p>}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>Drag onto calendar to schedule — {j.job_name || j.job_number}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Calendar Content */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : viewMode === 'day' ? (
              <DayView
                events={filteredEvents}
                selectedDate={selectedDate}
                displayPrefs={displayPrefs}
                canEdit={canEdit}
                hasCrm={hasCrm}
                readinessMap={readinessMap}
                onEdit={openEditDrawer}
                onDelete={(id) => deleteConfirm === id ? handleDelete(id) : setDeleteConfirm(id)}
                deleteConfirm={deleteConfirm}
                onCancelDelete={() => setDeleteConfirm(null)}
                onStatusChange={handleStatusChange}
                onQuickAdd={(jid) => setQuickAddJobId(jid)}
                onPrepareTruck={(jid) => setPrepareTruckJobId(jid)}
                navigate={navigate}
                onDragStartEvent={handleDragStartEvent}
                onCalendarDrop={handleCalendarDrop}
                onCalendarDragOver={handleCalendarDragOver}
                onDragLeave={handleDragLeave}
                dragOver={dragOver}
                onSendOtwText={handleSendOtwText}
                sendingOtwId={sendingOtwId}
                blockedTimes={blockedTimes}
              />
            ) : (
              <WeekView
                days={dateRange.days}
                eventsByDate={eventsByDate}
                displayPrefs={displayPrefs}
                canEdit={canEdit}
                navigate={navigate}
                onEdit={openEditDrawer}
                selectedDate={selectedDate}
                onDragStartEvent={handleDragStartEvent}
                onCalendarDrop={handleCalendarDrop}
                onCalendarDragOver={handleCalendarDragOver}
                onDragLeave={handleDragLeave}
                dragOver={dragOver}
                blockedTimes={blockedTimes}
              />
            )}

            {!loading && filteredEvents.length === 0 && viewMode === 'week' && (
              <Card className="mt-4">
                <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                  <CalendarDays className="w-10 h-10 text-muted-foreground/40 mb-3" />
                  <h3 className="font-semibold mb-1">No events scheduled</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {viewMode === 'week' ? 'No events for this week.' : 'No events for this day.'}
                  </p>
                  {canEdit && (
                    <Button onClick={openCreateDrawer} size="sm">
                      <Plus className="w-4 h-4 mr-1" /> Schedule Event
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Schedule Event Drawer */}
        <AppDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          title={editingEvent ? 'Edit Event' : 'Schedule Event'}
          description={editingEvent ? 'Update the dispatch event.' : 'Assign a job to a technician.'}
          data-testid="event-drawer"
          footer={
            <>
              <Button type="button" variant="outline" onClick={() => setDrawerOpen(false)}>Cancel</Button>
              <Button type="submit" form="event-form" disabled={saving} data-testid="save-event-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingEvent ? 'Update' : 'Schedule'}
              </Button>
            </>
          }
        >
          <form id="event-form" onSubmit={handleSave} className="space-y-4">
            {!editingEvent && (
              <JobSearchPicker
                jobs={jobs}
                value={form.job_id}
                onSelect={(id) => setForm({ ...form, job_id: id })}
              />
            )}
            <div>
              <Label>Technician</Label>
              <Select value={form.assigned_user_id || 'none'} onValueChange={(v) => setForm({ ...form, assigned_user_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="select-event-tech"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.role})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scheduled Date *</Label>
              <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} data-testid="input-event-date" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Time *</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="input-event-start-time" />
              </div>
              <div>
                <Label>End Time *</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="input-event-end-time" />
              </div>
            </div>
            {editingEvent && (
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="select-event-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes" data-testid="input-event-notes" />
            </div>
          </form>
        </AppDrawer>

        {/* Settings Drawer */}
        <AppDrawer
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          title="Display Settings"
          description="Control what information is shown on event blocks."
          data-testid="settings-drawer"
        >
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <Label>Show Customer Name</Label>
              <Switch
                checked={displayPrefs.showCustomer}
                onCheckedChange={(v) => setDisplayPrefs(p => ({ ...p, showCustomer: v }))}
                data-testid="pref-show-customer"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show Job Name</Label>
              <Switch
                checked={displayPrefs.showJobName}
                onCheckedChange={(v) => setDisplayPrefs(p => ({ ...p, showJobName: v }))}
                data-testid="pref-show-job-name"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show Time</Label>
              <Switch
                checked={displayPrefs.showTime}
                onCheckedChange={(v) => setDisplayPrefs(p => ({ ...p, showTime: v }))}
                data-testid="pref-show-time"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show Technician</Label>
              <Switch
                checked={displayPrefs.showTech}
                onCheckedChange={(v) => setDisplayPrefs(p => ({ ...p, showTech: v }))}
                data-testid="pref-show-tech"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Show Private Notes</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Job-level private notes on calendar cards</p>
              </div>
              <Switch
                checked={displayPrefs.showPrivateNotes}
                onCheckedChange={(v) => setDisplayPrefs(p => ({ ...p, showPrivateNotes: v }))}
                data-testid="pref-show-private-notes"
              />
            </div>
          </div>
        </AppDrawer>

        {/* Quick Add Material Modal */}
        {quickAddJobId && (
          <QuickAddMaterialModal jobId={quickAddJobId} open={!!quickAddJobId} onClose={() => setQuickAddJobId(null)} />
        )}

        {/* Prepare Truck Drawer */}
        <PrepareTruckDrawer
          open={!!prepareTruckJobId}
          onClose={() => setPrepareTruckJobId(null)}
          jobId={prepareTruckJobId}
          onRestocked={fetchEvents}
        />

        {/* Block Time Drawer */}
        <AppDrawer
          open={blockedTimeDrawerOpen}
          onOpenChange={setBlockedTimeDrawerOpen}
          title="Block Time"
          description="Mark a technician as unavailable for a time period."
          data-testid="block-time-drawer"
          footer={
            <>
              <Button variant="outline" onClick={() => setBlockedTimeDrawerOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveBlockedTime} disabled={savingBlockedTime} data-testid="save-blocked-time-btn">
                {savingBlockedTime && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Technician *</Label>
              <Select value={blockedTimeForm.user_id} onValueChange={(v) => setBlockedTimeForm(f => ({ ...f, user_id: v }))}>
                <SelectTrigger data-testid="bt-tech-select"><SelectValue placeholder="Select technician" /></SelectTrigger>
                <SelectContent>
                  {techs.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Title *</Label>
              <Input value={blockedTimeForm.title} onChange={e => setBlockedTimeForm(f => ({ ...f, title: e.target.value }))} placeholder="PTO, Training, Lunch..." data-testid="bt-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={blockedTimeForm.block_type} onValueChange={(v) => setBlockedTimeForm(f => ({ ...f, block_type: v }))}>
                <SelectTrigger data-testid="bt-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pto">PTO / Day Off</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="training">Training</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start *</Label>
                <Input type="datetime-local" value={blockedTimeForm.start_at} onChange={e => setBlockedTimeForm(f => ({ ...f, start_at: e.target.value }))} data-testid="bt-start-input" />
              </div>
              <div className="space-y-1.5">
                <Label>End *</Label>
                <Input type="datetime-local" value={blockedTimeForm.end_at} onChange={e => setBlockedTimeForm(f => ({ ...f, end_at: e.target.value }))} data-testid="bt-end-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={blockedTimeForm.notes || ''} onChange={e => setBlockedTimeForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes..." data-testid="bt-notes-input" />
            </div>
          </div>
        </AppDrawer>
      </div>
      {canEdit && <MobileFAB onClick={openCreateDrawer} data-testid="mobile-fab-dispatch" />}
    </Layout>
  );
}

/* =========================================================================
   POSITION HELPERS — compute top % and height % for a time block
   ========================================================================= */

const GRID_START = 0;  // 12 AM
const GRID_END   = 24; // 12 AM next day
const GRID_HOURS = 24; // full 24-hour range

const DAY_HOUR_PX  = 56; // height of each hour row in Day view
const WEEK_HOUR_PX = 40; // height of each hour row in Week view

/** Hook: returns current decimal hour in tenant timezone, updates every 60s */
function useCurrentTime() {
  const [now, setNow] = React.useState(() => getCurrentDecimalHour());
  React.useEffect(() => {
    const id = setInterval(() => setNow(getCurrentDecimalHour()), 60_000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function getCurrentDecimalHour() {
  const tz = getTenantTimezone();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false,
  }).formatToParts(new Date());
  const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
  const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
  return h + m / 60;
}

function getCurrentDateStr() {
  const tz = getTenantTimezone();
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(new Date()); // YYYY-MM-DD
  return parts;
}

/** Check if an event is currently active (overlaps the current time) */
function isEventActive(evt) {
  const now = Date.now();
  const start = parseUTC(evt.start_at).getTime();
  const end = parseUTC(evt.end_at).getTime();
  return now >= start && now < end;
}

function timeToPercent(isoStr) {
  const tz = getTenantTimezone();
  const d = parseUTC(isoStr);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false,
  }).formatToParts(d);
  const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10);
  const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
  return ((h + m / 60) / GRID_HOURS) * 100;
}

function blockStyle(evt) {
  const top = timeToPercent(evt.start_at);
  const bottom = timeToPercent(evt.end_at);
  const height = Math.max(bottom - top, 2); // min 2% so tiny events are still visible
  return { position: 'absolute', top: `${top}%`, height: `${height}%`, left: '4px', right: '4px' };
}

/** Resolve horizontal overlap — assign a "column" index so overlapping blocks sit side-by-side. */
function layoutOverlaps(events) {
  if (!events.length) return [];
  const sorted = [...events].sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const placed = [];

  for (const evt of sorted) {
    const start = new Date(evt.start_at).getTime();
    const end   = new Date(evt.end_at).getTime();

    // Find first column where there's no overlap
    let col = 0;
    while (placed.some(p => p.col === col && p.end > start)) col++;

    placed.push({ evt, col, start, end });
  }

  const maxCol = Math.max(0, ...placed.map(p => p.col)) + 1;
  return placed.map(p => ({
    evt: p.evt,
    style: {
      ...blockStyle(p.evt),
      left: `${(p.col / maxCol) * 100}%`,
      width: `${100 / maxCol}%`,
      paddingLeft: '4px', paddingRight: '4px',
    },
  }));
}


/* =========================================================================
   DAY VIEW — Single time-grid column, events positioned by time
   ========================================================================= */

function DayView({ events, selectedDate, displayPrefs, canEdit, hasCrm, readinessMap, onEdit, onDelete, deleteConfirm, onCancelDelete, onStatusChange, onQuickAdd, onPrepareTruck, navigate, onDragStartEvent, onCalendarDrop, onCalendarDragOver, onDragLeave, dragOver, onSendOtwText, sendingOtwId, blockedTimes = [] }) {
  const gridRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const laid = layoutOverlaps(events);
  const currentHour = useCurrentTime();
  const todayStr = getCurrentDateStr();
  const isToday = selectedDate === todayStr;
  const nowPct = ((Math.max(GRID_START, Math.min(currentHour, GRID_END)) - GRID_START) / GRID_HOURS) * 100;

  // Auto-scroll to current time area on load
  React.useEffect(() => {
    if (containerRef.current) {
      const scrollTarget = Math.max(0, currentHour - 2);
      const totalHeight = HOURS.length * 64; // matches h-16 = 64px per hour
      const scrollPx = (scrollTarget / GRID_HOURS) * totalHeight;
      containerRef.current.scrollTop = scrollPx;
    }
  }, [selectedDate]); // only on date change, not every minute

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="day-view">
      <div className="flex" ref={containerRef} style={{ maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
        {/* Time gutter */}
        <div className="w-16 shrink-0 border-r border-border">
          {HOURS.map(hour => (
            <div key={hour} className="h-16 border-b border-border last:border-b-0 flex items-start justify-end pr-2 pt-0.5">
              <span className="text-[10px] text-muted-foreground font-mono leading-none">
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </span>
            </div>
          ))}
        </div>

        {/* Calendar body — events positioned absolutely */}
        <div
          ref={gridRef}
          className="flex-1 relative"
          style={{ height: `${HOURS.length * 64}px` }}
          onDrop={(e) => gridRef.current && onCalendarDrop(e, selectedDate, gridRef.current)}
          onDragOver={(e) => gridRef.current && onCalendarDragOver(e, selectedDate, gridRef.current)}
          onDragLeave={onDragLeave}
          data-testid="day-view-drop-zone"
        >
          {/* Hour grid lines */}
          {HOURS.map(hour => (
            <div key={hour} className="absolute w-full border-b border-border" style={{ top: `${((hour - GRID_START) / GRID_HOURS) * 100}%`, height: 0 }} />
          ))}

          {/* Current-time indicator */}
          {isToday && currentHour >= GRID_START && currentHour <= GRID_END && (
            <div
              className="absolute w-full z-[15] pointer-events-none"
              style={{ top: `${nowPct}%` }}
              data-testid="current-time-indicator"
            >
              <div className="relative flex items-center">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0 -ml-[5px]" />
                <div className="flex-1 h-[2px] bg-red-500" />
              </div>
            </div>
          )}

          {/* Drop indicator line */}
          {dragOver && dragOver.date === selectedDate && (
            <div
              className="absolute w-full h-0.5 bg-primary z-20 pointer-events-none"
              style={{ top: `${((dragOver.hour - GRID_START) / GRID_HOURS) * 100}%` }}
              data-testid="drop-indicator"
            >
              <span className="absolute -top-3 left-2 text-[10px] font-medium bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                {formatSnapTime(dragOver.hour)}
              </span>
            </div>
          )}

          {/* Blocked time visual blocks */}
          {blockedTimes.filter(bt => {
            const btDate = new Date(bt.start_at).toISOString().slice(0, 10);
            return btDate === selectedDate;
          }).map(bt => {
            const startH = new Date(bt.start_at).getUTCHours() + new Date(bt.start_at).getUTCMinutes() / 60;
            const endH = new Date(bt.end_at).getUTCHours() + new Date(bt.end_at).getUTCMinutes() / 60;
            const topPct = (startH / GRID_HOURS) * 100;
            const heightPct = ((endH - startH) / GRID_HOURS) * 100;
            const BLOCK_TYPE_COLORS = {
              pto: 'bg-red-100/60 dark:bg-red-950/30 border-red-300 dark:border-red-800',
              personal: 'bg-orange-100/60 dark:bg-orange-950/30 border-orange-300 dark:border-orange-800',
              training: 'bg-indigo-100/60 dark:bg-indigo-950/30 border-indigo-300 dark:border-indigo-800',
              other: 'bg-gray-100/60 dark:bg-gray-900/30 border-gray-300 dark:border-gray-700',
            };
            const colorClass = BLOCK_TYPE_COLORS[bt.block_type] || BLOCK_TYPE_COLORS.other;
            return (
              <div
                key={`bt-${bt.id}`}
                data-testid={`blocked-time-${bt.id}`}
                className={`absolute z-[5] rounded border border-dashed pointer-events-none ${colorClass}`}
                style={{
                  top: `${topPct}%`,
                  height: `${Math.max(heightPct, 0.5)}%`,
                  left: '0', right: '0',
                  backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(0,0,0,0.03) 4px, rgba(0,0,0,0.03) 8px)',
                }}
              >
                <div className="px-2 py-0.5 flex items-center gap-1 overflow-hidden">
                  <span className="text-[10px] font-semibold text-muted-foreground truncate">
                    {bt.title}
                  </span>
                  {bt.user_name && (
                    <span className="text-[9px] text-muted-foreground/70 truncate">
                      ({bt.user_name})
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* Event blocks — positioned by time */}
          {laid.map(({ evt, style }) => {
            const active = isEventActive(evt);
            return (
              <div
                key={evt.id}
                style={style}
                className={`z-10 ${active ? 'ring-2 ring-primary ring-offset-1 rounded-md shadow-lg scale-[1.01]' : ''}`}
                draggable={canEdit}
                onDragStart={(e) => onDragStartEvent(e, evt)}
              >
                <EventHoverCard evt={evt} showPrivateNotes={displayPrefs.showPrivateNotes}>
                  <EventBlock
                    evt={evt}
                    displayPrefs={displayPrefs}
                    canEdit={canEdit}
                    hasCrm={hasCrm}
                    readiness={readinessMap[evt.job_id]}
                    onEdit={() => onEdit(evt)}
                    onDelete={() => onDelete(evt.id)}
                    deleteConfirm={deleteConfirm === evt.id}
                    onCancelDelete={onCancelDelete}
                    onStatusChange={onStatusChange}
                    onQuickAdd={() => onQuickAdd(evt.job_id)}
                    onPrepareTruck={() => onPrepareTruck(evt.job_id)}
                    navigate={navigate}
                    onSendOtwText={onSendOtwText}
                    sendingOtwId={sendingOtwId}
                    active={active}
                  />
                </EventHoverCard>
              </div>
            );
          })}

          {/* Empty state hint */}
          {events.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-sm text-muted-foreground/50">Drop a job here to schedule</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* =========================================================================
   WEEK VIEW — 7 day columns with time grid, blocks positioned by time
   ========================================================================= */

function WeekView({ days, eventsByDate, displayPrefs, canEdit, navigate, onEdit, selectedDate, onDragStartEvent, onCalendarDrop, onCalendarDragOver, onDragLeave, dragOver, blockedTimes = [] }) {
  const dayRefs = React.useRef({});
  const containerRef = React.useRef(null);
  const currentHour = useCurrentTime();
  const todayStr = getCurrentDateStr();
  const nowPct = ((Math.max(GRID_START, Math.min(currentHour, GRID_END)) - GRID_START) / GRID_HOURS) * 100;

  // Auto-scroll to current time on load
  React.useEffect(() => {
    if (containerRef.current) {
      const scrollTarget = Math.max(0, currentHour - 2);
      const totalHeight = HOURS.length * 48;
      const scrollPx = (scrollTarget / GRID_HOURS) * totalHeight;
      containerRef.current.scrollTop = scrollPx;
    }
  }, [selectedDate]);

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="week-view">
      {/* Fixed day headers */}
      <div className="flex border-b border-border">
        <div className="w-14 shrink-0 border-r border-border" />
        {days.map(day => {
          const isTodayTz = day === todayStr;
          return (
            <div key={day} className={`flex-1 min-w-0 h-12 border-r border-border/70 last:border-r-0 flex flex-col items-center justify-center ${isTodayTz ? 'bg-primary/10' : 'bg-muted/30'}`}>
              <p className={`text-[10px] font-medium ${isTodayTz ? 'text-primary' : 'text-muted-foreground'}`}>
                {new Date(day + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}
              </p>
              <p className={`text-sm font-bold leading-none ${isTodayTz ? 'text-primary' : ''}`}>
                {new Date(day + 'T12:00:00').getDate()}
              </p>
            </div>
          );
        })}
      </div>

      {/* Scrollable grid area */}
      <div ref={containerRef} style={{ maxHeight: '65vh', overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="flex">
          {/* Time gutter */}
          <div className="w-14 shrink-0 border-r border-border">
            {HOURS.map(hour => (
              <div key={hour} className="h-12 border-b border-border last:border-b-0 flex items-start justify-end pr-1.5 pt-0.5">
                <span className="text-[9px] text-muted-foreground font-mono leading-none">
                  {hour === 0 ? '12AM' : hour < 12 ? `${hour}AM` : hour === 12 ? '12PM' : `${hour - 12}PM`}
                </span>
              </div>
            ))}
          </div>

          {/* Day columns wrapper — relative for full-width time indicator */}
          <div className="flex-1 flex relative" style={{ height: `${HOURS.length * 48}px` }}>
            {/* Full-width current-time indicator */}
            {days.includes(todayStr) && currentHour >= GRID_START && currentHour <= GRID_END && (
              <div
                className="absolute inset-x-0 z-[15] pointer-events-none"
                style={{ top: `${nowPct}%` }}
                data-testid="week-current-time-indicator"
              >
                <div className="relative flex items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0 -ml-[5px]" />
                  <div className="flex-1 h-[2px] bg-red-500/70" />
                </div>
              </div>
            )}

            {/* Day columns */}
            {days.map(day => {
              const isTodayTz = day === todayStr;
              const evts = eventsByDate[day] || [];
              const laid = layoutOverlaps(evts);

              return (
                <div key={day} className={`flex-1 min-w-0 border-r border-border/70 last:border-r-0 ${isTodayTz ? 'bg-primary/[0.03]' : ''}`}>
                  {/* Time grid + events — drop target */}
                  <div
                    ref={(el) => { dayRefs.current[day] = el; }}
                    className={`relative h-full ${dragOver?.date === day ? 'bg-primary/5' : ''}`}
                    onDrop={(e) => dayRefs.current[day] && onCalendarDrop(e, day, dayRefs.current[day])}
                    onDragOver={(e) => dayRefs.current[day] && onCalendarDragOver(e, day, dayRefs.current[day])}
                    onDragLeave={onDragLeave}
                    data-testid={`week-drop-zone-${day}`}
                  >
                    {/* Grid lines */}
                    {HOURS.map(hour => (
                      <div key={hour} className="absolute w-full border-b border-border/70" style={{ top: `${((hour - GRID_START) / GRID_HOURS) * 100}%`, height: 0 }} />
                    ))}

                    {/* Drop indicator */}
                    {dragOver?.date === day && (
                      <div
                        className="absolute w-full h-0.5 bg-primary z-20 pointer-events-none"
                        style={{ top: `${((dragOver.hour - GRID_START) / GRID_HOURS) * 100}%` }}
                      >
                        <span className="absolute -top-3 left-0.5 text-[8px] font-medium bg-primary text-primary-foreground px-1 py-0.5 rounded">
                          {formatSnapTime(dragOver.hour)}
                        </span>
                      </div>
                    )}

                    {/* Blocked time blocks (week view) */}
                    {blockedTimes.filter(bt => {
                      const btDate = new Date(bt.start_at).toISOString().slice(0, 10);
                      return btDate === day;
                    }).map(bt => {
                      const startH = new Date(bt.start_at).getUTCHours() + new Date(bt.start_at).getUTCMinutes() / 60;
                      const endH = new Date(bt.end_at).getUTCHours() + new Date(bt.end_at).getUTCMinutes() / 60;
                      const topPct = (startH / GRID_HOURS) * 100;
                      const heightPct = ((endH - startH) / GRID_HOURS) * 100;
                      return (
                        <div
                          key={`bt-${bt.id}`}
                          data-testid={`week-blocked-time-${bt.id}`}
                          className="absolute z-[5] rounded border border-dashed pointer-events-none bg-red-100/40 dark:bg-red-950/20 border-red-300/50 dark:border-red-800/50"
                          style={{
                            top: `${topPct}%`, height: `${Math.max(heightPct, 0.5)}%`,
                            left: '1px', right: '1px',
                            backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.03) 3px, rgba(0,0,0,0.03) 6px)',
                          }}
                        >
                          <span className="text-[8px] font-semibold text-muted-foreground px-1 truncate block">{bt.title}</span>
                        </div>
                      );
                    })}

                    {/* Event blocks */}
                    {laid.map(({ evt, style }) => {
                      const active = isEventActive(evt);
                      return (
                        <div
                          key={evt.id}
                          style={style}
                          className={`z-10 ${active ? 'ring-2 ring-primary ring-offset-1 rounded shadow-lg scale-[1.01]' : ''}`}
                          draggable={canEdit}
                          onDragStart={(e) => onDragStartEvent(e, evt)}
                        >
                          <EventHoverCard evt={evt} showPrivateNotes={displayPrefs.showPrivateNotes}>
                            <WeekEventBlock evt={evt} displayPrefs={displayPrefs} navigate={navigate} onEdit={onEdit} active={active} />
                          </EventHoverCard>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


/* =========================================================================
   EVENT BLOCK — Day view (full detail)
   ========================================================================= */

function EventBlock({ evt, displayPrefs, canEdit, hasCrm, readiness, onEdit, onDelete, deleteConfirm, onCancelDelete, onStatusChange, onQuickAdd, onPrepareTruck, navigate, onSendOtwText, sendingOtwId }) {
  const isEstimate = evt.event_type === 'estimate';
  const statusStyle = STATUS_COLORS[evt.status] || '';

  return (
    <div
      className={`h-full rounded border-l-4 px-2 py-1 text-xs overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${isEstimate ? ESTIMATE_STATUS_COLOR : statusStyle}`}
      data-testid={`event-block-${evt.id}`}
      onClick={() => isEstimate ? navigate(`/estimates/${evt.estimate_id}`) : navigate(`/jobs/${evt.job_id}`)}
    >
      {/* Top row: type badge + number + status */}
      <div className="flex items-center gap-1 justify-between">
        <div className="flex items-center gap-1 min-w-0">
          {isEstimate ? (
            <span className="inline-flex items-center px-1 py-0 rounded text-[9px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400 shrink-0" data-testid={`event-type-badge-${evt.id}`}>Estimate</span>
          ) : (
            <span className="inline-flex items-center px-1 py-0 rounded text-[9px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 shrink-0" data-testid={`event-type-badge-${evt.id}`}>Job</span>
          )}
          <span
            className="font-semibold text-primary hover:underline truncate text-left text-xs cursor-pointer"
            data-testid={`event-job-link-${evt.id}`}
          >
            #{isEstimate ? evt.estimate_number : evt.job_number}
          </span>
          {!isEstimate && evt.estimate_id && (
            <span className="inline-flex items-center px-1 py-0 rounded text-[8px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 shrink-0" data-testid={`event-est-badge-${evt.id}`}>Estimate</span>
          )}
        </div>
        <EventStatusBadge status={evt.status} />
      </div>

      {/* Customer */}
      {displayPrefs.showCustomer && evt.customer_name && (
        <p className="text-muted-foreground truncate flex items-center gap-1 text-[11px]" data-testid={`event-customer-${evt.id}`}>
          <MapPin className="w-3 h-3 shrink-0" /> {evt.customer_name}
        </p>
      )}

      {/* Job name or Estimate name */}
      {displayPrefs.showJobName && (evt.job_name || (isEstimate && evt.estimate_name)) && (
        <p className="text-muted-foreground truncate text-[11px]" data-testid={`event-jobname-${evt.id}`}>{isEstimate ? evt.estimate_name : evt.job_name}</p>
      )}

      {/* Time */}
      {displayPrefs.showTime && (
        <p className="text-muted-foreground flex items-center gap-1 text-[11px]">
          <Clock className="w-3 h-3" /> {formatTime(evt.start_at)} — {formatTime(evt.end_at)}
        </p>
      )}

      {/* Tech */}
      {displayPrefs.showTech && evt.assigned_user_name && (
        <p className="text-muted-foreground flex items-center gap-1 text-[11px]">
          <User className="w-3 h-3" /> {evt.assigned_user_name}
        </p>
      )}

      {/* Private Notes */}
      {displayPrefs.showPrivateNotes && evt.job_notes && (
        <p className="text-muted-foreground/70 italic line-clamp-2 text-[10px] mt-0.5 flex items-start gap-1" data-testid={`event-notes-${evt.id}`}>
          <MessageSquare className="w-3 h-3 shrink-0 mt-px" />
          <span className="line-clamp-2">{evt.job_notes}</span>
        </p>
      )}

      {/* Operational badges row: plan + payment (secondary to status) */}
      {(evt.maintenance_plan_status || (evt.status === 'done' && evt.payment_status)) && (
        <div className="flex items-center gap-1 flex-wrap mt-0.5" data-testid={`event-ops-badges-${evt.id}`}>
          {evt.maintenance_plan_status && (() => {
            const pb = PLAN_BADGE_DISPATCH[evt.maintenance_plan_status];
            return pb ? (
              <span className={`inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-medium ${pb.className}`} data-testid={`event-plan-badge-${evt.id}`}>
                <Shield className="w-2.5 h-2.5" /> {pb.label}
              </span>
            ) : null;
          })()}
          {evt.status === 'done' && evt.payment_status && (() => {
            const pay = PAYMENT_BADGE[evt.payment_status];
            return pay ? (
              <span className={`inline-flex items-center gap-0.5 px-1 py-0 rounded text-[9px] font-medium ${pay.className}`} data-testid={`event-payment-badge-${evt.id}`}>
                <DollarSign className="w-2.5 h-2.5" /> {pay.label}
              </span>
            ) : null;
          })()}
        </div>
      )}

      {/* Readiness badge */}
      {readiness && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium cursor-help ${
                readiness.status === 'ready' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                readiness.status === 'missing_items' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                readiness.status === 'low_stock' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' :
                'bg-gray-100 text-gray-600'
              }`} data-testid={`readiness-badge-${evt.id}`}>
                {readiness.status === 'ready' && <Truck className="w-3 h-3" />}
                {readiness.status === 'missing_items' && <Wrench className="w-3 h-3" />}
                {readiness.status === 'low_stock' && <AlertTriangle className="w-3 h-3" />}
                {readiness.status_label}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {readiness.status === 'ready' ? 'Vehicle has all required parts.' :
               readiness.status === 'missing_items' ? 'Vehicle needs restocking.' :
               readiness.status === 'low_stock' ? 'Stock will be low after use.' :
               'No vehicle assigned.'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Actions — job events have full dispatch controls; estimates are view-only */}
      {canEdit && !isEstimate && (
        <div className="flex items-center gap-1 pt-0.5 border-t border-dashed mt-0.5" onClick={(e) => e.stopPropagation()}>
          {hasCrm && readiness && onPrepareTruck && (
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={(e) => { e.stopPropagation(); onPrepareTruck(); }}>
                <Truck className="w-3 h-3" />
              </Button>
            </TooltipTrigger><TooltipContent>Prepare vehicle for this job.</TooltipContent></Tooltip></TooltipProvider>
          )}
          {hasCrm && onQuickAdd && (
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={(e) => { e.stopPropagation(); onQuickAdd(); }}>
                <Package className="w-3 h-3" />
              </Button>
            </TooltipTrigger><TooltipContent>Quick-add material to this job.</TooltipContent></Tooltip></TooltipProvider>
          )}
          {evt.customer_name && onSendOtwText && (
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0"
                onClick={(e) => { e.stopPropagation(); onSendOtwText(evt.id); }}
                disabled={sendingOtwId === evt.id}
                data-testid={`send-otw-text-${evt.id}`}
              >
                {sendingOtwId === evt.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
              </Button>
            </TooltipTrigger><TooltipContent>Send &quot;On The Way&quot; text to customer</TooltipContent></Tooltip></TooltipProvider>
          )}
          <Select value={evt.status} onValueChange={(v) => onStatusChange(evt.id, v)}>
            <SelectTrigger className="h-5 text-[10px] w-[80px]" data-testid={`event-status-select-${evt.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <TooltipProvider><Tooltip><TooltipTrigger asChild>
            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={onEdit}>
              <Briefcase className="w-3 h-3" />
            </Button>
          </TooltipTrigger><TooltipContent>Edit event</TooltipContent></Tooltip></TooltipProvider>
          {deleteConfirm ? (
            <div className="flex gap-1 ml-auto">
              <Button variant="destructive" size="sm" className="h-5 text-[10px] px-1" onClick={onDelete} data-testid={`confirm-delete-event-${evt.id}`}>Del</Button>
              <Button variant="outline" size="sm" className="h-5 w-5 p-0" onClick={onCancelDelete}><X className="w-3 h-3" /></Button>
            </div>
          ) : (
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 ml-auto" onClick={onDelete} data-testid={`delete-event-${evt.id}`}>
                <Trash2 className="w-3 h-3 text-destructive" />
              </Button>
            </TooltipTrigger><TooltipContent>Delete event</TooltipContent></Tooltip></TooltipProvider>
          )}
        </div>
      )}
    </div>
  );
}


/* =========================================================================
   WEEK EVENT BLOCK — Compact block for week view
   ========================================================================= */

function WeekEventBlock({ evt, displayPrefs, navigate, onEdit }) {
  const isEstimate = evt.event_type === 'estimate';
  const statusStyle = isEstimate ? ESTIMATE_STATUS_COLOR : (STATUS_COLORS[evt.status] || '');

  return (
    <div
      className={`h-full rounded border-l-3 border-l-4 px-1 py-0.5 text-[9px] overflow-hidden cursor-pointer hover:shadow-sm transition-shadow ${statusStyle}`}
      onClick={() => isEstimate ? navigate(`/estimates/${evt.estimate_id}`) : navigate(`/jobs/${evt.job_id}`)}
      data-testid={`week-event-${evt.id}`}
    >
      <div className="flex items-center justify-between gap-0.5">
        <div className="flex items-center gap-0.5 min-w-0">
          {isEstimate ? (
            <span className="inline-flex items-center px-0.5 rounded text-[8px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400 shrink-0">E</span>
          ) : (
            <span className="inline-flex items-center px-0.5 rounded text-[8px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 shrink-0">J</span>
          )}
          <span className="font-bold truncate">#{isEstimate ? evt.estimate_number : evt.job_number}</span>
        </div>
        {!isEstimate && <EventStatusBadge status={evt.status} />}
      </div>
      {displayPrefs.showCustomer && evt.customer_name && (
        <p className="text-muted-foreground truncate">{evt.customer_name}</p>
      )}
      {displayPrefs.showTime && (
        <p className="text-muted-foreground">{formatTime(evt.start_at)}–{formatTime(evt.end_at)}</p>
      )}
      {displayPrefs.showTech && evt.assigned_user_name && (
        <p className="text-muted-foreground truncate flex items-center gap-0.5">
          <User className="w-2.5 h-2.5" /> {evt.assigned_user_name}
        </p>
      )}
      {displayPrefs.showPrivateNotes && evt.job_notes && (
        <p className="text-muted-foreground/70 italic truncate text-[8px]" data-testid={`week-notes-${evt.id}`}>
          {evt.job_notes}
        </p>
      )}
      {/* Compact operational badges for week view */}
      {(evt.maintenance_plan_status || (evt.status === 'done' && evt.payment_status)) && (
        <div className="flex items-center gap-0.5 flex-wrap">
          {evt.maintenance_plan_status && (() => {
            const pb = PLAN_BADGE_DISPATCH[evt.maintenance_plan_status];
            return pb ? <span className={`inline-flex items-center px-0.5 rounded text-[7px] font-medium ${pb.className}`} data-testid={`week-plan-badge-${evt.id}`}>{pb.label}</span> : null;
          })()}
          {evt.status === 'done' && evt.payment_status && (() => {
            const pay = PAYMENT_BADGE[evt.payment_status];
            return pay ? <span className={`inline-flex items-center px-0.5 rounded text-[7px] font-medium ${pay.className}`} data-testid={`week-payment-badge-${evt.id}`}>{pay.label}</span> : null;
          })()}
        </div>
      )}
    </div>
  );
}
